import classNames from 'classnames';
import React from 'react';
import {
  Button as ReactAriaButton,
  Dialog as ReactAriaDialog,
  DialogTrigger as ReactAriaDialogTrigger,
  DialogTriggerProps as ReactAriaDialogTriggerProps,
  Heading as ReactAriaHeading,
  HeadingProps,
  Modal as ReactAriaModal,
  ModalOverlay as ReactAriaModalOverlay,
  ModalOverlayProps as ReactAriaModalOverlayProps,
} from 'react-aria-components/Modal';

import Icon from '../../../app/shared/components/Icon/Icon';
import css from './Modal.css';

type ModalProps = Pick<ReactAriaDialogTriggerProps, 'children' | 'isOpen' | 'onOpenChange'>;

export const Modal = ({ ...props }: ModalProps) => {
  return <ReactAriaDialogTrigger {...props} />;
};

/**
 * Without a trigger to render, leave out `Modal` and pass `isOpen`/`onOpenChange` here instead —
 * `Modal` expects a pressable child and warns when it has none.
 */
interface ModalContentProps extends Pick<ReactAriaModalOverlayProps, 'isOpen' | 'onOpenChange'> {
  children: React.ReactNode;
  /**
   * Whether a click outside or the escape key closes the dialog. Turn it off for as long as the
   * dialog is doing something it has to report the outcome of — a modal that can vanish mid-request
   * leaves the user with no way of telling whether it went through.
   */
  isDismissable?: boolean;
  size?: 'small' | 'medium';
}

export const ModalContent = ({ children, isDismissable = true, size = 'medium', ...props }: ModalContentProps) => {
  return (
    <ReactAriaModalOverlay
      {...props}
      className={css.overlay}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={!isDismissable}
    >
      <ReactAriaModal className={classNames(css.modal, css[size])}>
        <ReactAriaDialog className={css.dialog}>{children}</ReactAriaDialog>
      </ReactAriaModal>
    </ReactAriaModalOverlay>
  );
};

interface ModalHeaderProps {
  children: React.ReactNode;
  closeLabel: string;
  className?: string;
  isCloseDisabled?: boolean;
}

export const ModalHeader = ({ children, className, closeLabel, isCloseDisabled }: ModalHeaderProps) => {
  return (
    <header className={classNames(css.header, className)}>
      {children}
      <ReactAriaButton className={css.closeButton} slot="close" aria-label={closeLabel} isDisabled={isCloseDisabled}>
        <Icon name="cross" className={css.icon} />
      </ReactAriaButton>
    </header>
  );
};

type ModalHeadingProps = Pick<HeadingProps, 'children' | 'className'>;

export const ModalHeading = (props: ModalHeadingProps) => {
  return <ReactAriaHeading slot="title" className={css.heading} {...props} />;
};

interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const ModalBody = ({ children, className, style }: ModalBodyProps) => {
  return (
    <div className={classNames(css.body, className)} style={style}>
      {children}
    </div>
  );
};

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const ModalFooter = ({ children, className }: ModalFooterProps) => {
  return <div className={classNames(css.footer, className)}>{children}</div>;
};
